import React from 'react';
import { StarIcon, TrophyIcon, GlobeIcon, UtensilsIcon, ShoppingBagIcon, MapIcon } from 'lucide-react';
interface TourManagerProps {
  manager: {
    name: string;
    title: string;
    photo: string;
    specializations: string[];
    experience: number;
    rating: number;
    rarity: string;
    stats: {
      toursCompleted: number;
      customerSatisfaction: number;
      languages: number;
    };
  };
  onClick?: () => void;
}
export const TourManagerCard: React.FC<TourManagerProps> = ({
  manager,
  onClick
}) => {
  // Determine card border color based on rarity
  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case 'bronze':
        return 'border-amber-700';
      case 'silver':
        return 'border-gray-400';
      case 'gold':
        return 'border-yellow-400';
      case 'platinum':
        return 'border-cyan-200';
      default:
        return 'border-gray-300';
    }
  };
  // Get icon for specialization
  const getSpecializationIcon = (spec: string) => {
    switch (spec.toLowerCase()) {
      case 'europe':
      case 'asia':
      case 'americas':
        return <GlobeIcon className="h-3 w-3" data-id="element-22" />;
      case 'food':
        return <UtensilsIcon className="h-3 w-3" data-id="element-23" />;
      case 'shopping':
        return <ShoppingBagIcon className="h-3 w-3" data-id="element-24" />;
      case 'adventure':
      case 'culture':
        return <MapIcon className="h-3 w-3" data-id="element-25" />;
      default:
        return <GlobeIcon className="h-3 w-3" data-id="element-26" />;
    }
  };
  return <div className={`relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg overflow-hidden 
        ${getRarityColor(manager.rarity)} border-4 shadow-xl hover:shadow-2xl 
        transition-all duration-300 ease-in-out transform hover:-translate-y-1 cursor-pointer`} onClick={onClick} data-id="element-27">
      {/* Rarity indicator */}
      <div className="absolute top-3 right-3 bg-black bg-opacity-60 rounded-full px-3 py-1 text-xs font-bold" data-id="element-28">
        {manager.rarity}
      </div>
      {/* Top card section with image */}
      <div className="relative h-64 overflow-hidden" data-id="element-29">
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10" data-id="element-30"></div>
        <img src={manager.photo} alt={manager.name} className="w-full h-full object-cover object-center" data-id="element-31" />
      </div>
      {/* Card content */}
      <div className="p-4" data-id="element-32">
        <h3 className="text-xl font-bold text-white" data-id="element-33">{manager.name}</h3>
        <p className="text-gray-300 text-sm mb-3" data-id="element-34">{manager.title}</p>
        {/* Specialization badges */}
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-35">
          {manager.specializations.map((spec, index) => <div key={index} className="flex items-center bg-blue-900 bg-opacity-60 text-blue-200 text-xs px-2 py-1 rounded-full" data-id="element-36">
              {getSpecializationIcon(spec)}
              <span className="ml-1" data-id="element-37">{spec}</span>
            </div>)}
        </div>
        {/* Rating */}
        <div className="flex items-center mb-3" data-id="element-38">
          <div className="flex" data-id="element-39">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < Math.floor(manager.rating) ? 'text-yellow-400' : 'text-gray-600'}`} fill={i < Math.floor(manager.rating) ? 'currentColor' : 'none'} data-id="element-40" />)}
          </div>
          <span className="text-yellow-400 text-sm ml-2" data-id="element-41">{manager.rating}</span>
        </div>
        {/* Experience level */}
        <div className="mb-4" data-id="element-42">
          <p className="text-xs text-gray-400 mb-1" data-id="element-43">Experience Level</p>
          <div className="w-full bg-gray-700 rounded-full h-2" data-id="element-44">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full" style={{
            width: `${manager.experience / 10 * 100}%`
          }} data-id="element-45"></div>
          </div>
        </div>
        {/* Stats section */}
        <div className="mt-4 pt-4 border-t border-gray-700" data-id="element-46">
          <div className="grid grid-cols-3 gap-2 text-center" data-id="element-47">
            <div className="bg-slate-800 rounded p-2" data-id="element-48">
              <p className="text-xs text-gray-400" data-id="element-49">Tours</p>
              <p className="text-lg font-bold text-white" data-id="element-50">
                {manager.stats.toursCompleted}
              </p>
            </div>
            <div className="bg-slate-800 rounded p-2" data-id="element-51">
              <p className="text-xs text-gray-400" data-id="element-52">Satisfaction</p>
              <p className="text-lg font-bold text-green-400" data-id="element-53">
                {manager.stats.customerSatisfaction}%
              </p>
            </div>
            <div className="bg-slate-800 rounded p-2" data-id="element-54">
              <p className="text-xs text-gray-400" data-id="element-55">Languages</p>
              <p className="text-lg font-bold text-blue-400" data-id="element-56">
                {manager.stats.languages}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};